import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ImportExportRounded, ArrowRightAltRounded, KeyboardArrowDownRounded, KeyboardArrowUpRounded, MoreVert } from "@mui/icons-material";
import FileSaverExcel from '../../../components/FileSaverExcel';
import SearchBarWebex from './SearchBarWebex';
import { useTranslation } from "react-i18next";
import TreeChart from './TreeChart/TreeChart';
import Loader from "../../../components/Loader";
import CallRecordingAudio from './CallRecordingAudio';
export default function TableComponentStatistics({ tableData, tableColumns, statusLoadingData, user }) {
    const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [firstCalls, setFirstCall] = useState([]);
    const [allCalls, setAllCalls] = useState({});
    const [allCallsFormatted, setAllCallsFormatted] = useState();
    const [filters, setFilters] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 100;
    const showAudio = [39, 18, 27].includes(Number(user?.entrepriseId));
    const columnCount = 12 + (user.holdingId === 33 ? 1 : 0) + (showAudio ? 1 : 0);
    const { t } = useTranslation();

    const formatCallOutcome = (call) => (
        call.calloutcome === "Success" ? t("Succès") :
            call.calloutcome === "Refusal" ? t("Refusé") :
                call.calloutcome === "Failure" ? t("Échec") : 'NA'
    );

    const isLikelyInternalNumber = (value) => /^\d{3,6}$/.test(String(value || '').trim());

    const formatCallType = (call) => (
        isLikelyInternalNumber(call.callingnumber) && isLikelyInternalNumber(call.callednumber) ? t('Interne') :
            call.direction === 'ORIGINATING' ? t('Sortant') :
                call.direction === 'TERMINATING' ? t('Entrant') : 'NA'
    );

    const formatAnswerIndicator = (call) => (
        call.answerindicator === "Yes" ? t("Oui") :
            call.answerindicator === "No" ? t("Non") :
                call.answerindicator === "Yes-PostRedirection" ? t("Redirection-d'appel") : 'NA'
    );

    const formatGroupedAnswerIndicator = (value) => (
        value === "Yes" ? t("Oui") :
            value === "No" ? t("Non") :
                value === "Yes-PostRedirection" ? t("Redirection-d'appel") : 'NA'
    );

    const isOutgoingScenario = (call) => call?.direction === 'ORIGINATING';
    const isIncomingScenarioCall = (call) => call?.direction === 'TERMINATING';
    const isTechnicalInfrastructureCall = (call) => (
        call?.usertype === 'LocalGateway' ||
        call?.clienttype === 'CISCO_SIP_GW'
    );

    const looksLikeNamedLineLabel = (value) => /ligne/i.test(String(value || '').trim());
    const looksLikeExternalPhoneNumber = (value) => /^\+?\d{7,}$/.test(String(value || '').replace(/\s+/g, ''));
    const looksLikeReadablePartyLabel = (value) => /[A-Za-zÀ-ÿ]/.test(String(value || '').trim());
    const looksLikeTechnicalTrunkLabel = (value) => /(cube|trunk|localgateway|gateway)/i.test(String(value || '').trim());
    const hasUsableSourceLineLabel = (call) => {
        const callingLineId = String(call?.callinglineid || '').trim();
        if (!callingLineId || callingLineId === 'NA') {
            return false;
        }

        if (isTechnicalInfrastructureCall(call)) {
            return false;
        }

        if (looksLikeTechnicalTrunkLabel(callingLineId)) {
            return false;
        }

        return looksLikeNamedLineLabel(callingLineId) || looksLikeExternalPhoneNumber(callingLineId);
    };
    const hasUsableSourceLineNumber = (call) => {
        const candidates = [call?.callingnumber, call?.calleridnumber];
        return candidates.some(candidate => looksLikeExternalPhoneNumber(candidate));
    };
    const hasUsableCalledLineLabel = (value) => {
        const normalizedValue = String(value || '').trim();
        if (!normalizedValue || normalizedValue === 'NA') {
            return false;
        }

        if (looksLikeTechnicalTrunkLabel(normalizedValue)) {
            return false;
        }

        return looksLikeReadablePartyLabel(normalizedValue) || looksLikeExternalPhoneNumber(normalizedValue);
    };
    const hasUsableBusinessUserLabel = (call) => {
        if (!call || isTechnicalInfrastructureCall(call)) {
            return false;
        }

        return hasUsableCalledLineLabel(call.user);
    };
    const normalizePhoneNumber = (value) => String(value || '').trim();
    const isUsableExternalPhoneNumber = (value) => {
        const normalizedValue = normalizePhoneNumber(value);
        return normalizedValue && looksLikeExternalPhoneNumber(normalizedValue);
    };
    const getOwnedIncomingNumbers = (calls) => {
        const ownedNumbers = new Set();

        (calls || []).forEach(call => {
            if (!call) {
                return;
            }

            const isOwnedNumberCarrier =
                call.calltype === 'SIP_INBOUND' ||
                call.usertype === 'CallCenterPremium';

            if (!isOwnedNumberCarrier) {
                return;
            }

            [call.usernumber, call.sitemainnumber, call.callednumber]
                .map(normalizePhoneNumber)
                .filter(isUsableExternalPhoneNumber)
                .forEach(number => ownedNumbers.add(number));
        });

        return ownedNumbers;
    };
    const resolveIncomingCalledNumber = (calls, inboundStandardEntryCall, incomingEntryCall) => {
        const ownedIncomingNumbers = getOwnedIncomingNumbers(calls);
        const priorityCalls = [
            inboundStandardEntryCall,
            incomingEntryCall,
            ...(calls || [])
        ].filter(Boolean);

        for (const call of priorityCalls) {
            const candidates = [call.usernumber, call.sitemainnumber, call.callednumber];
            for (const candidate of candidates) {
                const normalizedCandidate = normalizePhoneNumber(candidate);
                if (ownedIncomingNumbers.has(normalizedCandidate)) {
                    return normalizedCandidate;
                }
            }
        }

        return [...ownedIncomingNumbers][0] || '';
    };
    const resolveIncomingCallingNumber = (calls, resolvedCalledNumber) => {
        const ownedIncomingNumbers = getOwnedIncomingNumbers(calls);
        const protectedOwnedNumbers = new Set(
            [resolvedCalledNumber, ...ownedIncomingNumbers]
                .map(normalizePhoneNumber)
                .filter(Boolean)
        );

        const sortedCalls = [...(calls || [])].sort(compareCallsByStartTime);

        for (const call of sortedCalls) {
            const candidates = [
                call.externalcalleridnumber,
                call.calleridnumber,
                call.redirectingnumber,
                call.callingnumber,
            ];

            for (const candidate of candidates) {
                const normalizedCandidate = normalizePhoneNumber(candidate);
                if (!isUsableExternalPhoneNumber(normalizedCandidate)) {
                    continue;
                }

                if (!protectedOwnedNumbers.has(normalizedCandidate)) {
                    return normalizedCandidate;
                }
            }
        }

        return '';
    };
    const getBestIncomingTargetCall = (calls) => {
        if (!Array.isArray(calls) || calls.length === 0) {
            return null;
        }

        const sortedCalls = [...calls].sort(compareCallsByStartTime);
        const inboundEntryCall = sortedCalls.find(call =>
            call &&
            call.direction === 'TERMINATING' &&
            call.calltype === 'SIP_INBOUND'
        );

        const queueEntryCall = sortedCalls.find(call =>
            call &&
            call.direction === 'ORIGINATING' &&
            call.usertype === 'CallCenterPremium' &&
            hasUsableBusinessUserLabel(call)
        );

        return inboundEntryCall || queueEntryCall || null;
    };
    const getStrictInboundStandardCall = (calls) => {
        if (!Array.isArray(calls) || calls.length === 0) {
            return null;
        }

        return [...calls]
            .sort(compareCallsByStartTime)
            .find(call =>
                call &&
                call.direction === 'TERMINATING' &&
                call.calltype === 'SIP_INBOUND' &&
                hasUsableBusinessUserLabel(call)
            ) || null;
    };
    const getGatewayMirroredOutgoingPair = (calls) => {
        if (!Array.isArray(calls) || calls.length === 0) {
            return { userCall: null, gatewayCall: null };
        }

        const userCall = calls.find(call =>
            call &&
            isUserEndpointCall(call) &&
            call.direction === 'ORIGINATING' &&
            looksLikeExternalPhoneNumber(call.callednumber)
        ) || null;

        if (!userCall) {
            return { userCall: null, gatewayCall: null };
        }

        const gatewayCall = calls.find(call =>
            call &&
            isTechnicalInfrastructureCall(call) &&
            call.direction === 'TERMINATING' &&
            call.calltype === 'SIP_INBOUND' &&
            call.callednumber === userCall.callednumber &&
            (
                call.callingnumber === userCall.calleridnumber ||
                call.calleridnumber === userCall.calleridnumber ||
                call.callingnumber === userCall.callingnumber
            )
        ) || null;

        return { userCall, gatewayCall };
    };
    const getIncomingEntryLineLabel = (call) => {
        if (!call) {
            return '';
        }

        if (hasUsableBusinessUserLabel(call)) {
            return String(call.user || '').trim();
        }

        const calledLineLabel = String(call.calledlineid || '').trim();
        if (hasUsableCalledLineLabel(calledLineLabel)) {
            return calledLineLabel;
        }

        const callingLineLabel = String(call.callinglineid || '').trim();
        if (hasUsableCalledLineLabel(callingLineLabel)) {
            return callingLineLabel;
        }

        return '';
    };
    const resolveIncomingCalledLineLabel = (calls, calledNumber) => {
        if (!Array.isArray(calls) || calls.length === 0) {
            return '';
        }

        const normalizedCalledNumber = String(calledNumber || '').trim();
        const sortedCalls = [...calls].sort(compareCallsByStartTime);
        const matchingQueueStandardCall = sortedCalls.find(call =>
            call &&
            call.usertype === 'CallCenterPremium' &&
            hasUsableBusinessUserLabel(call) &&
            (
                !normalizedCalledNumber ||
                call.callednumber === normalizedCalledNumber ||
                call.sitemainnumber === normalizedCalledNumber ||
                call.usernumber === normalizedCalledNumber
            )
        );

        if (hasUsableBusinessUserLabel(matchingQueueStandardCall)) {
            return matchingQueueStandardCall.user;
        }

        const matchingInboundStandardCall = sortedCalls.find(call =>
            call &&
            call.calltype === 'SIP_INBOUND' &&
            hasUsableBusinessUserLabel(call) &&
            (
                !normalizedCalledNumber ||
                call.callednumber === normalizedCalledNumber ||
                call.sitemainnumber === normalizedCalledNumber ||
                call.usernumber === normalizedCalledNumber
            )
        );

        if (hasUsableBusinessUserLabel(matchingInboundStandardCall)) {
            return matchingInboundStandardCall.user;
        }

        const firstInboundStandardCall = sortedCalls.find(call =>
            call &&
            call.calltype === 'SIP_INBOUND' &&
            hasUsableBusinessUserLabel(call)
        );

        return hasUsableBusinessUserLabel(firstInboundStandardCall) ? firstInboundStandardCall.user : '';
    };

    const getLegFirstCall = (leg) => {
        const calls = leg?.calls || [];

        if (calls.length === 0) {
            return null;
        }

        const prioritizedCalls = calls.some(call => call.direction === 'ORIGINATING')
            ? calls.filter(call => call.direction === 'ORIGINATING')
            : calls;

        return [...prioritizedCalls].sort((callA, callB) => {
            const startA = callA?.starttime ? new Date(callA.starttime).getTime() : Number.MAX_SAFE_INTEGER;
            const startB = callB?.starttime ? new Date(callB.starttime).getTime() : Number.MAX_SAFE_INTEGER;
            return startA - startB;
        })[0] || null;
    };

    const compareCallsByStartTime = (callA, callB) => {
        const startA = callA?.starttime ? new Date(callA.starttime).getTime() : Number.MAX_SAFE_INTEGER;
        const startB = callB?.starttime ? new Date(callB.starttime).getTime() : Number.MAX_SAFE_INTEGER;
        return startA - startB;
    };

    const getLegChronologicalFirstCall = (leg) => {
        const calls = leg?.calls || [];

        if (calls.length === 0) {
            return null;
        }

        return [...calls].sort(compareCallsByStartTime)[0] || null;
    };

    const getSortedChildren = (leg) => [...(leg?.children || [])].sort((childA, childB) =>
        compareCallsByStartTime(getLegFirstCall(childA), getLegFirstCall(childB))
    );

    const isDeflectionCall = (call, firstCallStartTime) => {
        if (!call || call.redirectreason !== 'Deflection' || call.calloutcome !== 'Success') {
            return false;
        }

        if (firstCallStartTime === null || !call.starttime) {
            return true;
        }

        return new Date(call.starttime).getTime() !== firstCallStartTime;
    };

    const getLegDeflectionCall = (leg, firstCallStartTime) => {
        const deflectionCalls = (leg?.calls || []).filter(call =>
            isDeflectionCall(call, firstCallStartTime)
        );

        if (deflectionCalls.length === 0) {
            return null;
        }

        const prioritizedCalls = deflectionCalls.some(call => call.direction === 'ORIGINATING')
            ? deflectionCalls.filter(call => call.direction === 'ORIGINATING')
            : deflectionCalls;

        return [...prioritizedCalls].sort(compareCallsByStartTime)[0] || null;
    };

    const getLegAnsweredSuccessCall = (leg) => {
        const answeredSuccessCalls = (leg?.calls || []).filter(call =>
            call.calloutcome === 'Success' && call.answerindicator === 'Yes'
        );

        if (answeredSuccessCalls.length === 0) {
            return null;
        }

        const prioritizedCalls = answeredSuccessCalls.some(call => call.direction === 'ORIGINATING')
            ? answeredSuccessCalls.filter(call => call.direction === 'ORIGINATING')
            : answeredSuccessCalls;

        return [...prioritizedCalls].sort(compareCallsByStartTime)[0] || null;
    };

    const findDirectDeflectionChild = (leg, firstCallStartTime) => {
        const deflectionChildren = getSortedChildren(leg)
            .map(child => ({
                leg: child,
                call: getLegDeflectionCall(child, firstCallStartTime)
            }))
            .filter(item => item.call);

        if (deflectionChildren.length === 0) {
            return null;
        }

        return deflectionChildren.sort((itemA, itemB) =>
            compareCallsByStartTime(itemA.call, itemB.call)
        )[0];
    };

    const findAnsweredSuccessChild = (leg) => {
        const answeredChildren = getSortedChildren(leg)
            .map(child => ({
                leg: child,
                call: getLegAnsweredSuccessCall(child)
            }))
            .filter(item => item.call);

        if (answeredChildren.length === 0) {
            return null;
        }

        return answeredChildren.sort((itemA, itemB) =>
            compareCallsByStartTime(itemA.call, itemB.call)
        )[0];
    };

    const findOperationalRootLeg = (rootLeg, firstCallStartTime) => {
        const directDeflectionChild = findDirectDeflectionChild(rootLeg, firstCallStartTime);

        if (directDeflectionChild) {
            return rootLeg;
        }

        const bridgeChild = getSortedChildren(rootLeg).find(child => {
            const childCall = getLegFirstCall(child);

            if (!childCall || firstCallStartTime === null || !childCall.starttime) {
                return false;
            }

            return new Date(childCall.starttime).getTime() === firstCallStartTime;
        });

        return bridgeChild || rootLeg;
    };

    const findLastDeflectionData = (startingLeg, startingCall, firstCallStartTime) => {
        let currentLeg = startingLeg;
        let lastDeflectionLeg = startingLeg;
        let lastDeflectionCall = startingCall;

        while (currentLeg) {
            const nextDeflection = findDirectDeflectionChild(currentLeg, firstCallStartTime);

            if (!nextDeflection) {
                return {
                    leg: lastDeflectionLeg,
                    call: lastDeflectionCall
                };
            }

            lastDeflectionLeg = nextDeflection.leg;
            lastDeflectionCall = nextDeflection.call;
            currentLeg = nextDeflection.leg;
        }

        return {
            leg: lastDeflectionLeg,
            call: lastDeflectionCall
        };
    };

    const findDeflectionOverflowData = (rootLeg, firstCall) => {
        if (!rootLeg || !firstCall) {
            return null;
        }

        const firstCallStartTime = firstCall?.starttime ? new Date(firstCall.starttime).getTime() : null;
        const operationalRootLeg = findOperationalRootLeg(rootLeg, firstCallStartTime);
        const firstDeflectionData = findDirectDeflectionChild(operationalRootLeg, firstCallStartTime);

        if (!firstDeflectionData) {
            return {
                deflectionCall: null,
                overflowCall: null
            };
        }

        const lastDeflectionData = findLastDeflectionData(
            firstDeflectionData.leg,
            firstDeflectionData.call,
            firstCallStartTime
        );
        const lastDeflectionLeg = lastDeflectionData?.leg || null;
        const lastDeflectionCall = lastDeflectionData?.call || null;

        if (firstCall.answerindicator === 'No') {
            return {
                deflectionCall: lastDeflectionCall,
                overflowCall: null
            };
        }

        const overflowChild = lastDeflectionLeg ? findAnsweredSuccessChild(lastDeflectionLeg) : null;

        return {
            deflectionCall: lastDeflectionCall,
            overflowCall: overflowChild?.call || null
        };
    };

    const buildExportRow = (calls, firstCallReference, rootLeg) => {
        const sortedCalls = [...calls].sort((a, b) => new Date(a.starttime) - new Date(b.starttime));
        const firstCall = getLegFirstCall(rootLeg) || firstCallReference || sortedCalls[0];
        const deflectionData = findDeflectionOverflowData(rootLeg, firstCall);
        const deflectionLeg = deflectionData?.deflectionCall || null;
        const overflowCall = deflectionData?.overflowCall || null;
        const firstCallWaitDuration = deflectionLeg
            ? Math.max(0, Math.round((new Date(deflectionLeg.starttime) - new Date(firstCall.starttime)) / 1000))
            : 'N/A';
        const hasDisplayableOverflow = !!overflowCall;

        return {
            'Date de début': firstCall.starttime || 'NA',
            'Localisation': firstCall.location || 'NA',
            'Ligne appelante': firstCall.callinglineid || 'NA',
            'Numéro appelant': firstCall.callingnumber || 'NA',
            ...(user.holdingId === 33 && {
                'Numéro affiché': firstCall.calleridnumber || 'NA',
            }),
            'Numéro appelé': firstCall.callednumber || 'NA',
            'Ligne appelée': firstCall.calledlineid || 'NA',
            'Résultat de l\'appel': formatCallOutcome(firstCall),
            'Type d\'appel': formatCallType(firstCall),
            'Durée de communication (s)': firstCall.duration || 'NA',
            'Durée d\'attente (s)': firstCallWaitDuration,
            'Indicateur de réponse': formatAnswerIndicator(firstCall),
            'Débordement vers': hasDisplayableOverflow ? (overflowCall.calledlineid || '') : '',
            'Durée attente débordement': hasDisplayableOverflow ? (overflowCall.ringduration || '') : '',
            'Durée communication débordement': hasDisplayableOverflow ? (overflowCall.duration || '') : '',
        };
    };

    const hasValue = (value) => value !== undefined && value !== null && value !== '';
    const hasPositiveNumericValue = (value) => hasValue(value) && !Number.isNaN(Number(value)) && Number(value) > 0;

    const isUserEndpointCall = (call) => {
        if (!call) {
            return false;
        }

        return (
            call.usertype === 'User' ||
            call.clienttype === 'TEAMS_WXC_CLIENT'
        );
    };

    const findBestInternalPartyCall = (calls, extension, preferredDirection) => {
        const normalizedExtension = String(extension || '').trim();

        if (!normalizedExtension) {
            return null;
        }

        const matchingCalls = (calls || [])
            .filter(call =>
                call &&
                isUserEndpointCall(call) &&
                [
                    call.usernumber,
                    call.callingnumber,
                    call.callednumber,
                ].some(value => String(value || '').trim() === normalizedExtension)
            )
            .sort((callA, callB) => {
                const score = (call) =>
                    (String(call.usernumber || '').trim() === normalizedExtension ? 8 : 0) +
                    (call.direction === preferredDirection ? 4 : 0) +
                    (hasUsableCalledLineLabel(call.user) ? 3 : 0) +
                    (hasUsableCalledLineLabel(call.calledlineid) ? 2 : 0) +
                    (hasUsableCalledLineLabel(call.callinglineid) ? 1 : 0);

                return score(callB) - score(callA) || compareCallsByStartTime(callA, callB);
            });

        return matchingCalls[0] || null;
    };

    const findBestTransferTargetCall = (calls, transferOriginatingCall, transferTimeMs) => {
        if (!Array.isArray(calls) || !transferOriginatingCall) {
            return null;
        }

        const targetExtension = String(transferOriginatingCall.callednumber || '').trim();
        const targetLabel = String(transferOriginatingCall.calledlineid || '').trim();
        const sourceExtension = String(transferOriginatingCall.usernumber || '').trim();

        const candidates = calls
            .filter(call => {
                if (!call || !isUserEndpointCall(call) || call === transferOriginatingCall) {
                    return false;
                }

                if (transferTimeMs && new Date(call.starttime).getTime() < transferTimeMs) {
                    return false;
                }

                const matchesTarget =
                    (targetExtension && (
                        String(call.usernumber || '').trim() === targetExtension ||
                        String(call.callednumber || '').trim() === targetExtension ||
                        String(call.callingnumber || '').trim() === targetExtension
                    )) ||
                    (targetLabel && (
                        String(call.user || '').trim() === targetLabel ||
                        String(call.calledlineid || '').trim() === targetLabel ||
                        String(call.callinglineid || '').trim() === targetLabel
                    ));

                if (!matchesTarget) {
                    return false;
                }

                return String(call.usernumber || '').trim() !== sourceExtension;
            })
            .sort((callA, callB) => {
                const score = (call) =>
                    (call.direction === 'TERMINATING' ? 8 : 0) +
                    (String(call.usernumber || '').trim() === targetExtension ? 6 : 0) +
                    (call.answerindicator === 'Yes' ? 4 : 0) +
                    (hasPositiveNumericValue(call.ringduration) ? 2 : 0) +
                    (hasPositiveDuration(call.duration) ? 1 : 0);

                return score(callB) - score(callA) || compareCallsByStartTime(callA, callB);
            });

        return candidates[0] || null;
    };

    const createEntryId = (correlationId, legKey) => `${correlationId}::${legKey}`;

    const findPrimaryCommunicationCall = (rootLeg, firstCall, deflectionData) => {
        if (!rootLeg || !firstCall) {
            return null;
        }

        if (deflectionData?.overflowCall) {
            return deflectionData.overflowCall;
        }

        const firstCallStartTime = firstCall?.starttime ? new Date(firstCall.starttime).getTime() : null;
        const operationalRootLeg = findOperationalRootLeg(rootLeg, firstCallStartTime);
        const answeredChild = findAnsweredSuccessChild(operationalRootLeg);

        return answeredChild?.call || null;
    };

    const findAnsweredEndpointCall = (rootLeg) => {
        const answeredCalls = flattenLegs(rootLeg)
            .filter(call =>
                isUserEndpointCall(call) &&
                call.answerindicator === 'Yes' &&
                hasPositiveDuration(call.duration)
            )
            .sort(compareCallsByStartTime);

        if (answeredCalls.length === 0) {
            return null;
        }

        const scoredCalls = answeredCalls.sort((callA, callB) => {
            const score = (call) =>
                (call.calltype !== 'SIP_INBOUND' ? 4 : 0) +
                ((call.calledlineid && call.calledlineid !== 'NA') ? 2 : 0) +
                ((call.callinglineid && call.callinglineid !== 'NA') ? 1 : 0);

            return score(callB) - score(callA) || compareCallsByStartTime(callA, callB);
        });

        return scoredCalls[0] || null;
    };

    const hasPositiveDuration = (value) => hasValue(value) && !Number.isNaN(Number(value)) && Number(value) > 0;

    const isQueueWaitHangup = (firstCall, primaryCommunicationCall, hasDisplayableOverflow, answeredEndpointCall) => {
        if (!firstCall) {
            return false;
        }

        // Webex can answer the call with queue music. If the remote party hangs up before
        // any real recipient answers, the reported duration is actually waiting time.
        return (
            firstCall.releasingparty === 'Remote' &&
            firstCall.answerindicator === 'Yes' &&
            firstCall.direction === 'TERMINATING' &&
            firstCall.calltype === 'SIP_INBOUND' &&
            !hasDisplayableOverflow &&
            !primaryCommunicationCall &&
            !answeredEndpointCall &&
            (!firstCall.calledlineid || firstCall.calledlineid === 'NA')
        );
    };

    const findFallbackWaitCall = (rootLeg, firstCall) => {
        const flattenedCalls = flattenLegs(rootLeg)
            .filter(call => call !== firstCall)
            .sort(compareCallsByStartTime);

        return flattenedCalls.find(call => hasPositiveDuration(call.ringduration)) || null;
    };

    const getWaitDuration = (rootLeg, firstCall, hasDisplayableOverflow, deflectionCall, primaryCommunicationCall, answeredEndpointCall) => {
        if (!firstCall) {
            return 'N/A';
        }

        if (hasDisplayableOverflow) {
            if (firstCall.starttime && deflectionCall?.starttime) {
                const classicWaitDuration = Math.max(
                    0,
                    Math.round((new Date(deflectionCall.starttime) - new Date(firstCall.starttime)) / 1000)
                );

                return Math.min(classicWaitDuration, 20);
            }

            return 20;
        }

        let waitDuration = null;

        if (hasPositiveDuration(answeredEndpointCall?.ringduration)) {
            waitDuration = Number(answeredEndpointCall.ringduration);
        }

        if (waitDuration === null && hasPositiveDuration(primaryCommunicationCall?.ringduration)) {
            waitDuration = Number(primaryCommunicationCall.ringduration);
        }

        if (waitDuration === null && hasPositiveDuration(firstCall.ringduration)) {
            waitDuration = Number(firstCall.ringduration);
        }

        if (waitDuration === null) {
            const fallbackWaitCall = findFallbackWaitCall(rootLeg, firstCall);
            if (fallbackWaitCall) {
                waitDuration = Number(fallbackWaitCall.ringduration);
            }
        }

        if (waitDuration !== null) {
            return waitDuration;
        }

        if (hasValue(firstCall.ringduration)) {
            return firstCall.ringduration;
        }

        return 'N/A';
    };

    const getCommunicationDuration = (firstCall, primaryCommunicationCall, hasDisplayableOverflow, waitDuration, answeredEndpointCall) => {
        if (!firstCall) {
            return 'NA';
        }

        if (!answeredEndpointCall) {
            return 0;
        }

        if (isQueueWaitHangup(firstCall, primaryCommunicationCall, hasDisplayableOverflow, answeredEndpointCall)) {
            return 0;
        }

        if (firstCall.answerindicator === 'No') {
            return 0;
        }

        if (hasDisplayableOverflow) {
            return 0;
        }

        if (hasValue(answeredEndpointCall?.duration)) {
            return answeredEndpointCall.duration;
        }

        return hasValue(primaryCommunicationCall?.duration) ? primaryCommunicationCall.duration : (hasValue(firstCall.duration) ? firstCall.duration : 'NA');
    };

    const getOverflowWaitDuration = (deflectionCall, overflowCall, answeredEndpointCall) => {
        if (!deflectionCall) {
            return '';
        }

        if (hasPositiveNumericValue(overflowCall?.ringduration)) {
            return Number(overflowCall.ringduration);
        }

        if (!answeredEndpointCall && hasPositiveNumericValue(deflectionCall?.duration)) {
            return Number(deflectionCall.duration);
        }

        if (hasPositiveNumericValue(deflectionCall?.ringduration)) {
            return Number(deflectionCall.ringduration);
        }

        return hasValue(deflectionCall?.duration) ? deflectionCall.duration : '';
    };

    const getOverflowCommunicationDuration = (overflowCall, answeredEndpointCall) => {
        if (!answeredEndpointCall) {
            return 0;
        }

        if (hasValue(answeredEndpointCall?.duration)) {
            return answeredEndpointCall.duration;
        }

        return hasValue(overflowCall?.duration) ? overflowCall.duration : 0;
    };

    const getReadableTransferParty = (candidates) => {
        const normalizedCandidates = candidates
            .filter(hasValue)
            .map(value => String(value).trim())
            .filter(Boolean);

        const namedCandidate = normalizedCandidates.find(value => /[A-Za-zÀ-ÿ]/.test(value));
        if (namedCandidate) {
            return namedCandidate;
        }

        const extensionCandidate = normalizedCandidates.find(value => /^\+?\d{4,}$/.test(value));
        if (extensionCandidate) {
            return extensionCandidate;
        }

        return '';
    };

    const findTransferData = (calls) => {
        if (!Array.isArray(calls) || calls.length === 0) {
            return null;
        }

        const sortedCalls = [...calls].sort(compareCallsByStartTime);
        const transferOriginatingCall = [...sortedCalls]
            .filter(call =>
                isUserEndpointCall(call) &&
                call.direction === 'ORIGINATING' &&
                (
                    call.redirectreason === 'Deflection' ||
                    call.relatedreason === 'Deflection' ||
                    hasValue(call.calltransfertime)
                )
            )
            .sort(compareCallsByStartTime)
            .pop() || null;

        if (!transferOriginatingCall) {
            return null;
        }

        const transferTime = transferOriginatingCall.calltransfertime || transferOriginatingCall.starttime || '';
        const transferTimeMs = transferTime ? new Date(transferTime).getTime() : null;

        const transferInitiatorCall = [...sortedCalls]
            .filter(call =>
                call !== transferOriginatingCall &&
                isUserEndpointCall(call) &&
                call.answerindicator === 'Yes' &&
                call.usernumber &&
                call.usernumber === transferOriginatingCall.usernumber &&
                (!transferTimeMs || new Date(call.starttime).getTime() < transferTimeMs)
            )
            .sort(compareCallsByStartTime)
            .pop() || null;

        if (!transferInitiatorCall) {
            return null;
        }

        const transferTargetCall = findBestTransferTargetCall(sortedCalls, transferOriginatingCall, transferTimeMs);

        return {
            initiator: getReadableTransferParty([
                transferOriginatingCall?.user,
                transferOriginatingCall?.callinglineid,
                transferOriginatingCall?.usernumber,
                transferOriginatingCall?.callingnumber,
                transferInitiatorCall?.user,
                transferInitiatorCall?.callinglineid,
                transferInitiatorCall?.usernumber,
                transferInitiatorCall?.callingnumber,
            ]),
            target: getReadableTransferParty([
                transferOriginatingCall?.calledlineid,
                transferTargetCall?.user,
                transferTargetCall?.calledlineid,
                transferTargetCall?.callinglineid,
                transferOriginatingCall?.callednumber,
                transferTargetCall?.usernumber,
            ]),
            targetNumber: transferOriginatingCall?.callednumber || transferTargetCall?.usernumber || '',
            transferTime,
            waitDuration: (
                hasPositiveNumericValue(transferTargetCall?.ringduration)
                    ? Number(transferTargetCall.ringduration)
                    : (hasPositiveNumericValue(transferOriginatingCall?.ringduration) ? Number(transferOriginatingCall.ringduration) : '')
            ),
            communicationDuration: (
                hasPositiveDuration(transferTargetCall?.duration)
                    ? Number(transferTargetCall.duration)
                    : (hasPositiveDuration(transferOriginatingCall?.duration) ? Number(transferOriginatingCall.duration) : '')
            ),
        };
    };

    const buildNormalizedStatisticsRow = (rootLeg, additionalProps = {}) => {
        const flattenedCalls = flattenLegs(rootLeg);
        const sortedCalls = [...flattenedCalls].sort((a, b) => new Date(a.starttime) - new Date(b.starttime));
        const inboundStandardEntryCall = getStrictInboundStandardCall(sortedCalls);
        const hasInboundTerminatingEntry = sortedCalls.some(call =>
            call &&
            call.direction === 'TERMINATING' &&
            call.calltype === 'SIP_INBOUND'
        );
        const hasUserPostRedirection = sortedCalls.some(call =>
            call &&
            isUserEndpointCall(call) &&
            call.answerindicator === 'Yes-PostRedirection'
        );
        const isIncomingPostRedirectionScenario = hasInboundTerminatingEntry && hasUserPostRedirection;
        const hasDeflectionInCallFlow = sortedCalls.some(call =>
            call &&
            (
                call.redirectreason === 'Deflection' ||
                call.relatedreason === 'Deflection'
            )
        );
        const preferredUserCall = sortedCalls.find(call =>
            call &&
            isUserEndpointCall(call) &&
            (
                call.direction === 'ORIGINATING' ||
                call.answerindicator === 'Yes'
            )
        );
        const gatewayMirroredOutgoingPair = getGatewayMirroredOutgoingPair(sortedCalls);
        const isGatewayMirroredOutgoingScenario =
            !!gatewayMirroredOutgoingPair.userCall &&
            !!gatewayMirroredOutgoingPair.gatewayCall &&
            !hasDeflectionInCallFlow &&
            !inboundStandardEntryCall;
        const firstChronologicalCall = sortedCalls[0] || null;
        const firstCall = isGatewayMirroredOutgoingScenario
            ? gatewayMirroredOutgoingPair.userCall
            : (hasDeflectionInCallFlow && inboundStandardEntryCall)
            ? inboundStandardEntryCall
            : (preferredUserCall || getLegFirstCall(rootLeg) || sortedCalls[0]);
        const gatewayMirroredOutgoingSourceCall = isGatewayMirroredOutgoingScenario
            ? gatewayMirroredOutgoingPair.gatewayCall
            : null;

        if (!firstCall) {
            return additionalProps;
        }

        const deflectionData = findDeflectionOverflowData(rootLeg, firstCall);
        const deflectionCall = deflectionData?.deflectionCall || null;
        const overflowCall = deflectionData?.overflowCall || null;
        const primaryCommunicationCall = findPrimaryCommunicationCall(rootLeg, firstCall, deflectionData);
        const answeredEndpointCall = findAnsweredEndpointCall(rootLeg);
        const transferData = findTransferData(flattenedCalls);
        const incomingEntryCall = isGatewayMirroredOutgoingScenario
            ? null
            : getBestIncomingTargetCall(sortedCalls);
        const isIncomingScenario = !!incomingEntryCall;
        const internalOriginCall = sortedCalls.find(call =>
            call &&
            isUserEndpointCall(call) &&
            call.direction === 'ORIGINATING' &&
            isLikelyInternalNumber(call.usernumber || call.callingnumber)
        ) || null;
        const internalTargetCall = sortedCalls.find(call =>
            call &&
            isUserEndpointCall(call) &&
            call.direction === 'TERMINATING' &&
            isLikelyInternalNumber(call.usernumber || call.callednumber)
        ) || null;
        const isInternalScenario = !isIncomingScenario && !!internalOriginCall && !!internalTargetCall;
        const isSingleLegInternalOutgoingScenario =
            !isIncomingScenario &&
            !isInternalScenario &&
            firstCall.direction === 'ORIGINATING' &&
            isUserEndpointCall(firstCall) &&
            isLikelyInternalNumber(firstCall.usernumber || firstCall.callingnumber) &&
            isLikelyInternalNumber(firstCall.callednumber);
        const internalOriginNumber =
            internalOriginCall?.usernumber ||
            internalOriginCall?.callingnumber ||
            '';
        const internalTargetNumber =
            internalTargetCall?.usernumber ||
            internalOriginCall?.callednumber ||
            internalTargetCall?.callednumber ||
            '';
        const internalOriginPartyCall = isInternalScenario
            ? findBestInternalPartyCall(sortedCalls, internalOriginNumber, 'ORIGINATING')
            : null;
        const internalTargetPartyCall = isInternalScenario
            ? findBestInternalPartyCall(sortedCalls, internalTargetNumber, 'TERMINATING')
            : null;
        const hasDisplayableOverflow = !!overflowCall && !transferData;
        const overflowTargetCall = overflowCall || deflectionCall || null;
        const displayNumber = firstCall.calleridnumber || answeredEndpointCall?.calleridnumber || '';
        const firstChronologicalSourceCall = firstChronologicalCall && (
            hasUsableSourceLineLabel(firstChronologicalCall) ||
            hasUsableSourceLineNumber(firstChronologicalCall)
        )
            ? firstChronologicalCall
            : null;
        const firstSourceCall = sortedCalls.find(call =>
            call &&
            (
                hasUsableSourceLineLabel(call) ||
                hasUsableSourceLineNumber(call)
            )
        ) || null;
        const gatewaySourceCall = sortedCalls.find(call =>
            call &&
            call !== firstCall &&
            (
                call.clienttype === 'CISCO_SIP_GW' ||
                call.usertype === 'LocalGateway'
            ) &&
            (
                hasUsableSourceLineLabel(call) ||
                hasUsableSourceLineNumber(call)
            )
        ) || null;
        const displaySourceCall = sortedCalls
            .filter(call => {
                if (!call || call === firstCall) {
                    return false;
                }

                const hasSourceInfo =
                    hasUsableSourceLineLabel(call) ||
                    hasUsableSourceLineNumber(call);

                if (!hasSourceInfo) {
                    return false;
                }

                const usesDisplayNumber =
                    displayNumber &&
                    (
                        call.callingnumber === displayNumber ||
                        call.calleridnumber === displayNumber
                    );

                const looksLikeGatewayOrQueue =
                    call.direction === 'TERMINATING' ||
                    call.clienttype === 'CISCO_SIP_GW' ||
                    call.usertype === 'LocalGateway' ||
                    call.calltype === 'SIP_INBOUND' ||
                    (call.callinglineid || '').toLowerCase().includes('ligne');

                return usesDisplayNumber || looksLikeGatewayOrQueue;
            })
            .sort((callA, callB) => {
                const score = (call) =>
                    (hasUsableSourceLineLabel(call) ? 8 : 0) +
                    (looksLikeNamedLineLabel(call.callinglineid) ? 4 : 0) +
                    (call.clienttype === 'CISCO_SIP_GW' ? 2 : 0) +
                    (call.usertype === 'LocalGateway' ? 2 : 0) +
                    (call.callingnumber === displayNumber ? 1 : 0);

                return score(callB) - score(callA);
            })[0] || null;
        const fallbackSourceCall = sortedCalls.find(call =>
            call &&
            call !== firstCall &&
            (
                (displayNumber && (
                    call.callingnumber === displayNumber ||
                    call.calleridnumber === displayNumber
                )) ||
                call.clienttype === 'CISCO_SIP_GW' ||
                call.usertype === 'LocalGateway'
            )
        ) || null;
        const resolvedSourceCall = firstChronologicalSourceCall || firstSourceCall || gatewaySourceCall || displaySourceCall || fallbackSourceCall || null;
        const sourceLineNumber =
            (looksLikeExternalPhoneNumber(gatewayMirroredOutgoingSourceCall?.callingnumber) ? gatewayMirroredOutgoingSourceCall.callingnumber : '') ||
            (looksLikeExternalPhoneNumber(resolvedSourceCall?.callingnumber) ? resolvedSourceCall.callingnumber : '') ||
            (looksLikeExternalPhoneNumber(resolvedSourceCall?.calleridnumber) ? resolvedSourceCall.calleridnumber : '') ||
            (looksLikeExternalPhoneNumber(displayNumber) ? displayNumber : '') ||
            (looksLikeExternalPhoneNumber(firstCall.calleridnumber) ? firstCall.calleridnumber : '') ||
            '';
        const sourceLineLabel =
            (isGatewayMirroredOutgoingScenario && hasUsableCalledLineLabel(gatewayMirroredOutgoingSourceCall?.callinglineid) ? gatewayMirroredOutgoingSourceCall.callinglineid : '') ||
            (hasUsableSourceLineLabel(resolvedSourceCall) ? resolvedSourceCall.callinglineid : '') ||
            sourceLineNumber ||
            '';
        const baseWaitDuration = getWaitDuration(rootLeg, firstCall, hasDisplayableOverflow, deflectionCall, primaryCommunicationCall, answeredEndpointCall);
        const waitDuration = isQueueWaitHangup(firstCall, primaryCommunicationCall, hasDisplayableOverflow, answeredEndpointCall)
            ? (hasValue(firstCall.duration) ? firstCall.duration : baseWaitDuration)
            : baseWaitDuration;
        const transferWaitDurationNumeric = hasValue(transferData?.waitDuration) ? Number(transferData.waitDuration) : 0;
        const transferCommunicationDurationNumeric = hasValue(transferData?.communicationDuration) ? Number(transferData.communicationDuration) : 0;
        const initialCommunicationDuration = transferData && hasValue(firstCall.duration)
            ? Math.max(0, Number(firstCall.duration) - transferWaitDurationNumeric - transferCommunicationDurationNumeric)
            : getCommunicationDuration(firstCall, primaryCommunicationCall, hasDisplayableOverflow, waitDuration, answeredEndpointCall);
        const initialWaitDuration = transferData && hasPositiveNumericValue(firstCall.ringduration)
            ? Number(firstCall.ringduration)
            : waitDuration;
        const resolvedCallingLineId = isGatewayMirroredOutgoingScenario
            ? (
                (hasUsableCalledLineLabel(firstCall.user) ? firstCall.user : '') ||
                sourceLineLabel ||
                ''
            )
            : isInternalScenario
            ? (
                (hasUsableCalledLineLabel(internalOriginPartyCall?.user) ? internalOriginPartyCall.user : '') ||
                (hasUsableCalledLineLabel(internalOriginPartyCall?.callinglineid) ? internalOriginPartyCall.callinglineid : '') ||
                (hasUsableCalledLineLabel(internalOriginPartyCall?.calledlineid) ? internalOriginPartyCall.calledlineid : '') ||
                (isLikelyInternalNumber(internalOriginNumber) ? internalOriginNumber : '') ||
                ''
            )
            : isIncomingScenarioCall(firstCall)
            ? ''
            : (
                firstCall.callinglineid && firstCall.callinglineid !== 'NA'
                    ? firstCall.callinglineid
                    : (firstCall.direction === 'ORIGINATING' && isUserEndpointCall(firstCall) ? (firstCall.user || '') : '')
            );
        const resolvedCalledNumber = isInternalScenario
            ? (
                internalTargetNumber ||
                internalTargetPartyCall?.usernumber ||
                internalTargetPartyCall?.callednumber ||
                ''
            )
            : isIncomingScenario
            ? (
                resolveIncomingCalledNumber(sortedCalls, inboundStandardEntryCall, incomingEntryCall) ||
                firstCall.callednumber ||
                answeredEndpointCall?.usernumber ||
                ''
            )
            : (
                firstCall.callednumber ||
                answeredEndpointCall?.usernumber ||
                primaryCommunicationCall?.callednumber ||
                ''
            );
        const strictInboundStandardCall = isIncomingScenario
            ? inboundStandardEntryCall
            : null;
        const incomingResolvedLineLabel = isIncomingScenario
            ? (strictInboundStandardCall?.user || resolveIncomingCalledLineLabel(sortedCalls, resolvedCalledNumber))
            : '';
        const incomingCalledLineId = isIncomingScenario
            ? (
                (hasUsableCalledLineLabel(incomingResolvedLineLabel) ? incomingResolvedLineLabel : '') ||
                (hasUsableBusinessUserLabel(incomingEntryCall) ? incomingEntryCall.user : '') ||
                getIncomingEntryLineLabel(incomingEntryCall) ||
                (hasUsableBusinessUserLabel(firstCall) ? firstCall.user : '') ||
                (hasUsableBusinessUserLabel(primaryCommunicationCall) ? primaryCommunicationCall.user : '') ||
                ''
            )
            : '';
        const resolvedCalledLineId = isInternalScenario
            ? (
                (hasUsableCalledLineLabel(internalTargetPartyCall?.user) ? internalTargetPartyCall.user : '') ||
                (hasUsableCalledLineLabel(internalTargetPartyCall?.calledlineid) ? internalTargetPartyCall.calledlineid : '') ||
                (hasUsableCalledLineLabel(internalTargetPartyCall?.callinglineid) ? internalTargetPartyCall.callinglineid : '') ||
                (isLikelyInternalNumber(resolvedCalledNumber) ? resolvedCalledNumber : '') ||
                ''
            )
            : isSingleLegInternalOutgoingScenario
            ? (
                (hasUsableCalledLineLabel(firstCall.calledlineid) ? firstCall.calledlineid : '') ||
                (isLikelyInternalNumber(resolvedCalledNumber) ? resolvedCalledNumber : '') ||
                'NA'
            )
            : isIncomingScenario
            ? incomingCalledLineId
            : (
                isOutgoingScenario(firstCall)
                    ? (
                        (looksLikeExternalPhoneNumber(resolvedCalledNumber) ? resolvedCalledNumber : '') ||
                        'NA'
                    )
                    : (
                        (hasUsableCalledLineLabel(firstCall.calledlineid) ? firstCall.calledlineid : '') ||
                        (firstCall.direction === 'TERMINATING' && isUserEndpointCall(firstCall) && hasUsableCalledLineLabel(firstCall.user) ? firstCall.user : '') ||
                        (hasUsableCalledLineLabel(answeredEndpointCall?.calledlineid) ? answeredEndpointCall.calledlineid : '') ||
                        (hasUsableCalledLineLabel(answeredEndpointCall?.user) ? answeredEndpointCall.user : '') ||
                        (hasUsableCalledLineLabel(primaryCommunicationCall?.calledlineid) ? primaryCommunicationCall.calledlineid : '') ||
                        (hasUsableCalledLineLabel(primaryCommunicationCall?.user) ? primaryCommunicationCall.user : '') ||
                        ''
                    )
            );
        const overflowCommunicationDuration = hasDisplayableOverflow
            ? (getOverflowCommunicationDuration(overflowCall, answeredEndpointCall) || primaryCommunicationCall?.duration || '')
            : '';

        return {
            ...firstCall,
            direction: isIncomingPostRedirectionScenario ? 'TERMINATING' : firstCall.direction,
            callingnumber: (
                isGatewayMirroredOutgoingScenario
                    ? (sourceLineNumber || firstCall.calleridnumber || firstCall.callingnumber || '')
                    : isInternalScenario
                    ? (internalOriginNumber || internalOriginPartyCall?.usernumber || internalOriginPartyCall?.callingnumber || '')
                    : (isIncomingScenario ? resolveIncomingCallingNumber(sortedCalls, resolvedCalledNumber) : '')
            ) || firstCall.callingnumber || 'NA',
            callinglineid: resolvedCallingLineId || 'NA',
            callednumber: resolvedCalledNumber || 'NA',
            calledlineid: resolvedCalledLineId || 'NA',
            calleridnumber: sourceLineNumber || displayNumber || displaySourceCall?.callingnumber || 'NA',
            duration: initialCommunicationDuration,
            ringduration: initialWaitDuration,
            sourceLineLabel,
            sourceLineNumber,
            isTransferred: !!transferData,
            transferInitiator: transferData?.initiator || '',
            transferTarget: transferData?.target || '',
            transferTargetNumber: transferData?.targetNumber || '',
            transferTime: transferData?.transferTime || '',
            transferWaitDuration: transferData?.waitDuration || '',
            transferCommunicationDuration: transferData?.communicationDuration || '',
            overflowTarget: hasDisplayableOverflow ? (overflowTargetCall?.calledlineid || overflowTargetCall?.user || overflowTargetCall?.callednumber || '') : '',
            overflowWaitDuration: hasDisplayableOverflow ? getOverflowWaitDuration(deflectionCall, overflowCall, answeredEndpointCall) : '',
            overflowCommunicationDuration,
            ...additionalProps
        };
    };

    const buildNormalizedExportRow = (rootLeg, additionalProps = {}) => {
        const formattedCall = buildNormalizedStatisticsRow(rootLeg, additionalProps);

        return {
            'Date de début': formattedCall.starttime || 'NA',
            'Localisation': formattedCall.location || 'NA',
            'Ligne appelante': formattedCall.callinglineid || 'NA',
            'Numéro appelant': formattedCall.callingnumber || 'NA',
            ...(user.holdingId === 33 && {
                'Numéro affiché': formattedCall.calleridnumber || 'NA',
            }),
            'Numéro appelé': formattedCall.callednumber || 'NA',
            'Ligne appelée': formattedCall.calledlineid || 'NA',
            'Résultat de l\'appel': formatCallOutcome(formattedCall),
            'Type d\'appel': formatCallType(formattedCall),
            'Durée de communication (s)': formattedCall.duration ?? 'NA',
            'Durée d\'attente (s)': formattedCall.ringduration,
            'Indicateur de réponse': formatGroupedAnswerIndicator(formattedCall.isAnswered),
            'Transféré': formattedCall.isTransferred ? 'Oui' : 'Non',
            'Transféré vers': formattedCall.isTransferred ? formattedCall.transferTarget : '',
            'Numéro cible transfert': formattedCall.isTransferred ? formattedCall.transferTargetNumber : '',
            'Heure de transfert': formattedCall.isTransferred ? formattedCall.transferTime : '',
            'Durée attente post-transfert (s)': formattedCall.isTransferred ? formattedCall.transferWaitDuration : '',
            'Durée communication post-transfert (s)': formattedCall.isTransferred ? formattedCall.transferCommunicationDuration : '',
            'Débordement vers': formattedCall.overflowTarget,
            'Durée attente débordement': formattedCall.overflowWaitDuration,
            'Durée communication débordement': formattedCall.overflowCommunicationDuration,
        };
    };

    const buildSafeExportRowFromFormattedCall = (formattedCall) => {
        const [callDate = 'NA', callTime = 'NA'] = formattedCall.starttime
            ? formattedCall.starttime.replace('T', ' ').split(' ')
            : ['NA', 'NA'];

        return {
            'Date d\u2019appel': callDate,
            'Heure d\u2019appel': callTime,
            'Localisation': formattedCall.location || 'NA',
            'Ligne appelante': formattedCall.callinglineid || 'NA',
            'Ligne utilisée': formattedCall.sourceLineNumber || formattedCall.calleridnumber || formattedCall.sourceLineLabel || 'NA',
            'Num\u00E9ro appelant': formattedCall.callingnumber || 'NA',
            ...(user.holdingId === 33 && {
                'Num\u00E9ro affich\u00E9': formattedCall.calleridnumber || 'NA',
            }),
            'Num\u00E9ro appel\u00E9': formattedCall.callednumber || 'NA',
            'Ligne appel\u00E9e': formattedCall.calledlineid || 'NA',
            'R\u00E9sultat de l\'appel': formatCallOutcome(formattedCall),
            'Type d\'appel': formatCallType(formattedCall),
            'Dur\u00E9e de communication (s)': formattedCall.duration ?? 'NA',
            'Dur\u00E9e d\'attente (s)': formattedCall.ringduration,
            'Indicateur de r\u00E9ponse': formatGroupedAnswerIndicator(formattedCall.isAnswered),
            'Transféré': formattedCall.isTransferred ? 'Oui' : 'Non',
            'Transféré vers': formattedCall.isTransferred ? formattedCall.transferTarget : '',
            'Numéro cible transfert': formattedCall.isTransferred ? formattedCall.transferTargetNumber : '',
            'Heure de transfert': formattedCall.isTransferred ? formattedCall.transferTime : '',
            'Durée attente post-transfert (s)': formattedCall.isTransferred ? formattedCall.transferWaitDuration : '',
            'Durée communication post-transfert (s)': formattedCall.isTransferred ? formattedCall.transferCommunicationDuration : '',
            'D\u00E9bordement vers': formattedCall.overflowTarget,
            'Dur\u00E9e attente d\u00E9bordement': formattedCall.overflowWaitDuration,
            'Dur\u00E9e communication d\u00E9bordement': formattedCall.overflowCommunicationDuration,
        };
    };

    const buildNormalizedExportRowSafe = (rootLeg, additionalProps = {}) => {
        const formattedCall = buildNormalizedStatisticsRow(rootLeg, additionalProps);
        return buildSafeExportRowFromFormattedCall(formattedCall);
    };

    const getTopLevelLegTimeKey = (leg, fallbackKey) => {
        const firstCall = getLegChronologicalFirstCall(leg) || getLegFirstCall(leg) || leg?.calls?.[0];
        return firstCall?.starttime || fallbackKey;
    };

    const shouldMergeOverflowLegGroups = (legs) => {
        if (!Array.isArray(legs) || legs.length <= 1) {
            return false;
        }

        const allCalls = legs.flatMap(leg => flattenLegs(leg));

        const hasDeflection = allCalls.some(call =>
            call &&
            (
                call.redirectreason === 'Deflection' ||
                call.relatedreason === 'Deflection'
            )
        );

        const hasInboundEntry = allCalls.some(call =>
            call &&
            call.calltype === 'SIP_INBOUND' &&
            call.direction === 'TERMINATING'
        );

        const gatewayMirroredOutgoingPair = getGatewayMirroredOutgoingPair(allCalls);
        const hasStrictInboundBusinessEntry = allCalls.some(call =>
            call &&
            call.direction === 'TERMINATING' &&
            call.calltype === 'SIP_INBOUND' &&
            hasUsableBusinessUserLabel(call)
        );

        return (
            (hasDeflection && hasInboundEntry) ||
            (
                !!gatewayMirroredOutgoingPair.userCall &&
                !!gatewayMirroredOutgoingPair.gatewayCall &&
                !hasDeflection &&
                !hasStrictInboundBusinessEntry
            )
        );
    };

    const selectRepresentativeLeg = (legs) => {
        if (!legs || legs.length === 0) {
            return null;
        }

        return [...legs].sort((legA, legB) => {
            const firstCallA = getLegFirstCall(legA) || legA?.calls?.[0];
            const firstCallB = getLegFirstCall(legB) || legB?.calls?.[0];
            const scoreA =
                (hasValue(firstCallA?.duration) ? 1 : 0) +
                (hasValue(firstCallA?.ringduration) ? 1 : 0) +
                flattenLegs(legA).length;
            const scoreB =
                (hasValue(firstCallB?.duration) ? 1 : 0) +
                (hasValue(firstCallB?.ringduration) ? 1 : 0) +
                flattenLegs(legB).length;

            return scoreB - scoreA;
        })[0];
    };

    useEffect(() => {
        if (statusLoadingData && tableData) {
            let { firstCallsData, allCallsData } = filterDataByFirstCall(tableData);
            let flattenedAllCalls = Object.entries(allCallsData).flatMap(([entryId, leg]) =>
                flattenLegs(leg).map(call => ({
                    ...call,
                    entryId,
                }))
            );

            if (filters.length > 0) {
                const firstCallsById = new Map(firstCallsData.map(call => [call.entryId, call]));
                const callsByEntryId = flattenedAllCalls.reduce((acc, call) => {
                    if (!acc[call.entryId]) {
                        acc[call.entryId] = [];
                    }
                    acc[call.entryId].push(call);
                    return acc;
                }, {});

                const filteredGroupIds = new Set(
                    Object.keys(callsByEntryId).filter(entryId =>
                        filters.every(filter => {
                            if (filter.column === 'isAnswered') {
                                const firstCall = firstCallsById.get(entryId);
                                return firstCall ? filter.values.includes(firstCall.isAnswered) : false;
                            }

                            return callsByEntryId[entryId].some(call =>
                                filter.values.includes(call[filter.column])
                            );
                        })
                    )
                );

                firstCallsData = firstCallsData.filter(item => filteredGroupIds.has(item.entryId));
                flattenedAllCalls = flattenedAllCalls.filter(item => filteredGroupIds.has(item.entryId));
            }

            // Formater les données aplaties pour affichage ou exportation
            const formattedData = firstCallsData.map(call =>
                buildSafeExportRowFromFormattedCall(call)
            );
            setAllCallsFormatted(formattedData);
            setFirstCall(firstCallsData);
            setAllCalls(allCallsData);
            setCurrentPage(1);
        }
    }, [filters, tableData, statusLoadingData]);
    const getChildLegs = (children) => {
        if (!children) {
            return [];
        }

        return Array.isArray(children) ? children : Object.values(children);
    };

    const flattenLegs = (leg) => {
        // Conserver tous les appels du leg actuel (premier niveau) sans filtrage
        const currentLegCalls = leg.calls || [];
        // Filtrer et aplatir les appels des enfants avec direction 'ORIGINATING'
        const childrenCalls = getChildLegs(leg.children).flatMap(child => flattenLegs(child));
        // Filtrer et aplatir les appels des subchildren avec direction 'ORIGINATING'
        const subchildrenCalls = getChildLegs(leg.subchildren).flatMap(subchild => flattenLegs(subchild));
        // Retourner les appels du leg actuel concaténés avec les enfants et subchildren filtrés
        return currentLegCalls.concat(childrenCalls, subchildrenCalls);
    };
    const flattenChildrenWithOriginating = (leg) => {
        // Filtrer uniquement les appels avec direction 'ORIGINATING'
        const originatingCalls = leg.calls ? leg.calls.filter(call => call.direction === 'ORIGINATING') : [];
        // Continuer de manière récursive avec les enfants et subchildren
        const childrenCalls = getChildLegs(leg.children).flatMap(flattenChildrenWithOriginating);
        const subchildrenCalls = getChildLegs(leg.subchildren).flatMap(flattenChildrenWithOriginating);
        // Retourner les appels filtrés des enfants et subchildren
        return originatingCalls.concat(childrenCalls, subchildrenCalls);
    };
    const mergeLegStructures = (legs, correlationId) => ({
        calls: [],
        children: legs,
        correlationId
    });
    const filterDataByFirstCall = useCallback((data) => {
        let firstCallsData = [];
        let allCallsData = {};
        data.forEach(entry => {
            const correlationId = entry.correlationid;
            const topLevelLegs = Object.values(entry.legs || {});
            const legsByTime = Object.entries(entry.legs).reduce((acc, [legKey, leg]) => {
                if (!leg.calls?.length) {
                    return acc;
                }

                const timeKey = getTopLevelLegTimeKey(leg, legKey);
                if (!acc[timeKey]) {
                    acc[timeKey] = [];
                }

                acc[timeKey].push(leg);
                return acc;
            }, {});

            const groupedLegEntries = shouldMergeOverflowLegGroups(topLevelLegs)
                ? [[getTopLevelLegTimeKey(selectRepresentativeLeg(topLevelLegs), correlationId), topLevelLegs]]
                : Object.entries(legsByTime);

            groupedLegEntries.forEach(([timeKey, groupedLegs]) => {
                const selectedLeg = selectRepresentativeLeg(groupedLegs);

                if (!selectedLeg) {
                    return;
                }

                const entryId = createEntryId(correlationId, timeKey);
                const legStructures = groupedLegs.map(leg => extractAllCalls(leg, correlationId));
                const legStructure = mergeLegStructures(legStructures, correlationId);

                allCallsData[entryId] = legStructure;
                firstCallsData.push(buildNormalizedStatisticsRow(legStructure, {
                    entryId,
                    correlationId,
                    isAnswered: checkAnswerIndicator(legStructure)
                }));
            });
        });
        return { firstCallsData, allCallsData };
    }, []);
    const getRelevantAnsweredUserCalls = (data) => {
        const flattenNodeCalls = (node) => {
            if (!node) {
                return [];
            }

            const currentCalls = Array.isArray(node.calls) ? node.calls : [];
            const childrenCalls = getChildLegs(node.children).flatMap(flattenNodeCalls);
            const subchildrenCalls = getChildLegs(node.subchildren).flatMap(flattenNodeCalls);

            return [...currentCalls, ...childrenCalls, ...subchildrenCalls];
        };

        return flattenNodeCalls(data).filter(call =>
            isUserEndpointCall(call) &&
            call.relatedreason !== 'PushNotificationRetrieval'
        );
    };
    const checkAnswerIndicator = useCallback((data) => {
        const relevantUserCalls = getRelevantAnsweredUserCalls(data);

        if (relevantUserCalls.some(call => call.answerindicator === "Yes-PostRedirection")) {
            return "Yes-PostRedirection";
        }

        if (relevantUserCalls.some(call => call.answerindicator === "Yes")) {
            return "Yes";
        }

        return "No";
    }, []);
    const extractAllCalls = useCallback((leg, correlationId) => {
        const legStructure = {
            calls: leg.calls?.map(call => ({ ...call, correlationId })) || [],
            children: leg.children ? Object.values(leg.children).map(child => extractAllCalls(child, correlationId)) : [],
            correlationId: correlationId
        };
        if (leg.subchildren) {
            legStructure.children = legStructure.children.concat(
                Object.values(leg.subchildren).map(subchild => extractAllCalls(subchild, correlationId))
            );
        }
        return legStructure;
    }, []);
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.column === key) {
            direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
        }
        setSortConfig({ column: key, direction });
    };
    const sortedData = () => {
        let filteredData = firstCalls;

        if (sortConfig.column !== null) {
            filteredData = filteredData.sort((a, b) => {
                if (a[sortConfig.column] < b[sortConfig.column]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.column] > b[sortConfig.column]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return filteredData;
    };
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        return sortedData().slice(start, end);
    }, [sortedData, currentPage, itemsPerPage]);
    const toggleRowExpansion = useCallback((entryId) => {
        setExpandedRows(prevRows => {
            const newRows = new Set(prevRows);
            newRows.has(entryId) ? newRows.delete(entryId) : newRows.add(entryId);
            return newRows;
        });
    }, []);
    const handleThClick = () => {
        setExpandedRows(prevRows => {
            if (prevRows.size === 0) {
                const allRows = new Set(paginatedData.map(row => row.entryId));
                return allRows;
            } else {
                return new Set();
            }
        });
    };
    const hasGreenNode = (leg) => {
        if (leg.calls.some(call => call.answerindicator === 'Yes')) return true;
        return leg.children?.some(child => hasGreenNode(child)) || false;
    };
    const handlePreviousPage = () => {
        setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
    };
    const handleNextPage = () => {
        setCurrentPage(prevPage => Math.min(prevPage + 1, totalPages));
    };
    const totalPages = Math.ceil(sortedData().length / itemsPerPage);
    return (
        <div className="container-listing-statistics">
            <div className="page-table-webex-header">
                <h2>{t('Historique détaillé des appels')}</h2>
                <div className='tooltip-csv-tableWebex'>
                    <MoreVert />
                    <span className='tooltip-csv-tableWebex-button'><FileSaverExcel data={[allCallsFormatted]} name={'Detailed call history'} /></span>
                </div>
            </div>
            <div>
                <SearchBarWebex dataWebex={firstCalls} dataWebexColumns={tableColumns} filters={filters} setFilters={setFilters} statusAnswered={true} />
            </div>
            <section className="statistics-device-container">
                <div className="statistics-table">
                    <div className="statistics-table-wrapper">
                        <table cellPadding="0" cellSpacing="0" border="0">
                            <thead className="statistics-table-wrapper-header">
                                <tr>
                                    <th onClick={handleThClick}>{expandedRows.size > 0 ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}</th>
                                    {tableColumns.map((item, index) => {
                                        if (item.accessor === 'calleridnumber' && user.holdingId !== 33) {
                                            return null;
                                        }
                                        return (
                                            <React.Fragment key={index}>
                                                {item.headerColumn && (
                                                    <th key={index}>
                                                        <div className="statistics-table-wrapper-header-content" onClick={() => requestSort(item.accessor)}>
                                                            {t(item.Header)}
                                                            <div className='statistics-table-wrapper-header-content-container-icons'>
                                                                {sortConfig.column !== item.accessor && (
                                                                    <span className='statistics-table-wrapper-header-content-icon'>
                                                                        <ImportExportRounded />
                                                                    </span>
                                                                )}
                                                                <span>
                                                                    {sortConfig.column === item.accessor && (
                                                                        sortConfig.direction === 'asc' ? (
                                                                            <span className='statistics-table-wrapper-header-content-icon-asc'><ArrowRightAltRounded /></span>
                                                                        ) : sortConfig.direction === 'desc' && (
                                                                            <span className='statistics-table-wrapper-header-content-icon-desc'><ArrowRightAltRounded /></span>
                                                                        )
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </th>
                                                )}
                                            </React.Fragment>
                                        )
                                    })}
                                    {showAudio && <th scope="col">{t('Audio')}</th>}
                                </tr>
                            </thead>
                            <tbody className="statistics-table-wrapper-content">
                                {statusLoadingData ? (
                                    firstCalls.length > 0 ? (
                                        paginatedData.map((data) => (
                                            <React.Fragment key={data.entryId}>
                                                <tr>
                                                    <td className='statistics-table-wrapper-content-element'>
                                                        <div className='legsCallingOpen' onClick={() => toggleRowExpansion(data.entryId)}>
                                                            {expandedRows.has(data.entryId) ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
                                                        </div>
                                                    </td>
                                                    <td className='statistics-table-wrapper-content-element'>{data.starttime || 'NA'}</td>
                                                    <td className='statistics-table-wrapper-content-element'>{data.location || 'NA'}</td>
                                                    <td className='statistics-table-wrapper-content-element'>{data.callinglineid || 'NA'}</td>
                                                    <td className='statistics-table-wrapper-content-element'>{data.callingnumber || 'NA'}</td>
                                                    {user.holdingId === 33 && (
                                                        <td className='statistics-table-wrapper-content-element'>
                                                            {data.calleridnumber || 'NA'}
                                                        </td>
                                                    )}
                                                    <td className='statistics-table-wrapper-content-element'>{data.calledlineid || 'NA'}</td>
                                                    <td className='statistics-table-wrapper-content-element'>{data.callednumber || 'NA'}</td>
                                                    <td className='statistics-table-wrapper-content-element'>
                                                        {
                                                            data.calloutcome === "Success" ? t("Succès") :
                                                                data.calloutcome === "Refusal" ? t("Refusé") :
                                                                    data.calloutcome === "Failure" ? t("Échec") : 'NA'
                                                        }
                                                    </td>
                                                    <td className='statistics-table-wrapper-content-element'>
                                                        {formatCallType(data)}
                                                    </td>
                                                    <td className='statistics-table-wrapper-content-element'>{data.duration ?? 'NA'}</td>
                                                    <td className='statistics-table-wrapper-content-element'>{data.ringduration ?? 'NA'}</td>
                                                    <td className={`statistics-table-wrapper-content-element ${data.isAnswered === 'Yes' ? 'statistics-table-wrapper-content-element-color-green' : data.isAnswered === 'Yes-PostRedirection' ? 'statistics-table-wrapper-content-element-color-refusal' : 'statistics-table-wrapper-content-element-color-red'}`}>
                                                        {
                                                            data.isAnswered === "Yes" ? t("Oui") :
                                                                data.isAnswered === "No" ? t("Non") : t("Redirection-d'appel")
                                                                    || 'NA'
                                                        }
                                                    </td>
                                                    {showAudio && (
                                                        <td className='statistics-table-wrapper-content-element'>
                                                            <CallRecordingAudio key={`${user.entrepriseId}:${data.correlationId}`} correlationId={data.correlationId} />
                                                        </td>
                                                    )}
                                                </tr>
                                                {expandedRows.has(data.entryId) && (
                                                    <tr key={`expanded-row-${data.entryId}`}>
                                                        <td colSpan={columnCount} className="expanded-details">
                                                            <TreeChart data={allCalls[data.entryId]} user={user} />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={columnCount} className='noAvailableDataCalling'>
                                                {t("No data available")}
                                            </td>
                                        </tr>
                                    )
                                ) : (
                                    <tr>
                                        <td colSpan={columnCount} className='noAvailableDataCalling'>
                                            <Loader />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="pagination-controls-webex-statistics">
                        <button onClick={handlePreviousPage} disabled={currentPage === 1}>{t('Précédent')}</button>
                        <span>Page {currentPage} {t('sur')} {totalPages}</span>
                        <button onClick={handleNextPage} disabled={currentPage === totalPages}>{t('Suivant')}</button>
                    </div>
                </div>
            </section>
        </div>
    );
}

